import * as XLSX from "xlsx";

/**
 * Generic Excel exporter for the Trial Balance family of reports.
 *
 * Mirrors the on-screen grouping (Node only, or Node → Group Code),
 * including subtotal rows and a grand total row, so the exported
 * workbook matches what the user sees in the table.
 *
 * @param {Object}   params
 * @param {Array}    params.data              Raw report rows to export (already filtered, if needed).
 * @param {Array}    params.columns           Visible column defs: [{ column_key_from_api, column_name }, ...]
 *                                            in the exact order they should appear in Excel.
 * @param {Array}    params.groupKeys         Row field(s) to group by, in order.
 *                                            ["NODE"] for single-level grouping,
 *                                            ["NODE", "GROUP CODE"] for two-level grouping.
 * @param {Array}    params.valueKeys         Numeric column keys that should be subtotalled
 *                                            (e.g. ["DEBIT", "CREDIT"] or ["OPENBAL","DEBIT","CREDIT","BALANCE"]).
 * @param {Boolean}  [params.deriveFromBalance=false]  When true, DEBIT/CREDIT values (for both the
 *                                            row cells and the subtotals) are derived from the sign
 *                                            of row.BALANCE instead of being read directly from
 *                                            row.DEBIT / row.CREDIT.
 * @param {String}   [params.fileName="TrialBalance"]  Base file name (date is appended automatically).
 * @param {String}   [params.sheetName="Report"]       Worksheet tab name.
 * @param {Boolean}  [params.appendBalanceColumn=false]  When true (and valueKeys includes both
 *                                            "DEBIT" and "CREDIT"), an extra "Balance" column is
 *                                            appended after the visible columns, showing DEBIT − CREDIT
 *                                            for every data row, subtotal, and the grand total.
 */
export function exportTrialBalanceToExcel({
  data,
  columns,
  groupKeys,
  valueKeys,
  deriveFromBalance = false,
  fileName = "TrialBalance",
  sheetName = "Report",
  appendBalanceColumn = false,
}) {
  if (!data || data.length === 0 || !columns || columns.length === 0) {
    return;
  }

  const round2 = (n) => Number((n || 0).toFixed(2));
  const showBalanceCol = appendBalanceColumn && valueKeys.includes("DEBIT") && valueKeys.includes("CREDIT");
  const rowWidth = columns.length + (showBalanceCol ? 1 : 0);

  // Resolve the display value for a single cell of a data row
  const getCellValue = (row, key) => {
    if (deriveFromBalance && (key === "DEBIT" || key === "CREDIT")) {
      const bal = row.BALANCE || 0;
      if (key === "DEBIT") return bal > 0 ? round2(bal) : "";
      if (key === "CREDIT") return bal < 0 ? round2(Math.abs(bal)) : "";
    }
    const v = row[key];
    return typeof v === "number" ? round2(v) : v ?? "";
  };

  // Sum a set of rows across all valueKeys
  const sumRows = (rows) => {
    const totals = {};
    valueKeys.forEach((k) => (totals[k] = 0));
    rows.forEach((row) => {
      valueKeys.forEach((k) => {
        if (deriveFromBalance && (k === "DEBIT" || k === "CREDIT")) {
          const bal = row.BALANCE || 0;
          if (k === "DEBIT" && bal > 0) totals.DEBIT += bal;
          if (k === "CREDIT" && bal < 0) totals.CREDIT += Math.abs(bal);
        } else {
          totals[k] += row[k] || 0;
        }
      });
    });
    return totals;
  };

  const indentLabel = (label, indent) => "    ".repeat(indent) + label;

  const buildDataRow = (row) => {
    const cells = columns.map((col) => getCellValue(row, col.column_key_from_api));
    if (showBalanceCol) {
      const debit = deriveFromBalance ? (row.BALANCE > 0 ? row.BALANCE : 0) : row.DEBIT || 0;
      const credit = deriveFromBalance ? (row.BALANCE < 0 ? Math.abs(row.BALANCE) : 0) : row.CREDIT || 0;
      cells.push(round2(debit - credit));
    }
    return cells;
  };

  const buildLabelRow = (label, indent = 0) => {
    const row = new Array(rowWidth).fill("");
    row[0] = indentLabel(label, indent);
    return row;
  };

  const buildTotalsRow = (label, totals, indent = 0) => {
    const row = new Array(rowWidth).fill("");
    row[0] = indentLabel(label, indent);
    columns.forEach((col, i) => {
      const key = col.column_key_from_api;
      if (valueKeys.includes(key)) {
        row[i] = round2(totals[key]);
      }
    });
    if (showBalanceCol) {
      row[columns.length] = round2((totals.DEBIT || 0) - (totals.CREDIT || 0));
    }
    return row;
  };

  const headerRow = columns.map((c) => c.column_name);
  if (showBalanceCol) headerRow.push("Balance");
  const wsData = [headerRow];

  if (groupKeys.length === 2) {
    const [outerKey, innerKey] = groupKeys;
    const grouped = data.reduce((acc, row) => {
      const k1 = row[outerKey];
      const k2 = row[innerKey];
      if (!acc[k1]) acc[k1] = {};
      if (!acc[k1][k2]) acc[k1][k2] = [];
      acc[k1][k2].push(row);
      return acc;
    }, {});

    Object.entries(grouped).forEach(([node, groups]) => {
      wsData.push(buildLabelRow(node));
      const nodeRows = Object.values(groups).flat();

      Object.entries(groups).forEach(([groupCode, rows]) => {
        wsData.push(buildLabelRow(String(groupCode), 1));
        rows.forEach((row) => wsData.push(buildDataRow(row)));
        wsData.push(buildTotalsRow(`Subtotal — ${groupCode}`, sumRows(rows), 1));
      });

      wsData.push(buildTotalsRow(`Node Total — ${node}`, sumRows(nodeRows)));
    });
  } else {
    const groupKey = groupKeys[0];
    const grouped = data.reduce((acc, row) => {
      const k = row[groupKey];
      if (!acc[k]) acc[k] = [];
      acc[k].push(row);
      return acc;
    }, {});

    Object.entries(grouped).forEach(([node, rows]) => {
      wsData.push(buildLabelRow(node));
      rows.forEach((row) => wsData.push(buildDataRow(row)));
      wsData.push(buildTotalsRow(`Subtotal — ${node}`, sumRows(rows)));
    });
  }

  wsData.push(buildTotalsRow("Grand Total", sumRows(data)));

  const ws = XLSX.utils.aoa_to_sheet(wsData);
  const colWidths = columns.map((col) => ({
    wch: ["DEBIT", "CREDIT", "BALANCE", "OPENBAL", "OPENDr", "OPENCr"].includes(col.column_key_from_api)
      ? 16
      : 28,
  }));
  if (showBalanceCol) colWidths.push({ wch: 16 });
  ws["!cols"] = colWidths;

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName.substring(0, 31));

  const safeFileName = fileName.replace(/[\\/:*?"<>|]/g, "").replace(/\s+/g, "_");
  const dateStamp = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `${safeFileName}_${dateStamp}.xlsx`);
}
